import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

/**
 * Centralized logging service for the application.
 * Provides consistent logging with environment-aware behavior and future
 * integration points for Application Insights or other logging services.
 */
@Injectable({
  providedIn: 'root'
})
export class LoggerService {
  private readonly isProduction = environment.serviceHostName.includes('texasshowpass.com');

  /**
   * Log informational messages.
   * In production, these are suppressed unless explicitly enabled.
   */
  log(message: string, ...args: unknown[]): void {
    if (!this.isProduction) {
      console.log(`[INFO] ${message}`, ...args);
    }
  }

  /**
   * Log warning messages.
   * Always logged, even in production.
   */
  warn(message: string, ...args: unknown[]): void {
    console.warn(`[WARN] ${message}`, ...args);
    // Future: Send to Application Insights
  }

  /**
   * Log error messages.
   * Always logged and should be sent to monitoring services.
   */
  error(message: string, error?: Error | unknown, ...args: unknown[]): void {
    console.error(`[ERROR] ${message}`, error, ...args);
    // Future: Send to Application Insights
    // this.appInsights.trackException({ exception: error as Error, properties: { message, ...args } });
  }

  /**
   * Log debug messages.
   * Only logged in development environments.
   */
  debug(message: string, ...args: unknown[]): void {
    if (!this.isProduction) {
      console.debug(`[DEBUG] ${message}`, ...args);
    }
  }

  /**
   * Log a trace for performance monitoring.
   * Useful for tracking timing-sensitive operations.
   */
  trace(message: string, ...args: unknown[]): void {
    if (!this.isProduction) {
      console.trace(`[TRACE] ${message}`, ...args);
    }
  }

  /**
   * Start a performance timer.
   * Returns a function to end the timer and log the duration.
   */
  startTimer(label: string): () => void {
    const start = performance.now();
    return () => {
      const duration = performance.now() - start;
      this.debug(`${label} took ${duration.toFixed(2)}ms`);
    };
  }
}
